export type FpixelFunction = (...args: unknown[]) => void;

type FpixelQueueFunction = FpixelFunction & {
  callMethod?: FpixelFunction;
  loaded: boolean;
  push: FpixelFunction;
  queue: unknown[][];
  version: string;
};

type FpixelWindow = {
  fbq?: FpixelQueueFunction;
  _fbq?: FpixelQueueFunction;
  __oneTranscribeFpixelLoaded?: boolean;
};

export type FpixelClientDependencies = {
  fpixel?: FpixelFunction;
  load?: () => void;
  canTrack?: () => boolean;
  getWindow?: () => FpixelWindow | undefined;
  appendScript?: (src: string) => void;
};

const _browserWindow = (): FpixelWindow | undefined =>
  typeof window === "undefined"
    ? undefined
    : (window as unknown as FpixelWindow);

const _appendBrowserScript = (src: string): void => {
  if (typeof document === "undefined") return;
  const script = document.createElement("script");
  script.async = true;
  script.src = src;
  document.head.appendChild(script);
};

const _createQueue = (): FpixelQueueFunction => {
  const queue = ((...args: unknown[]) => {
    if (queue.callMethod) queue.callMethod(...args);
    else queue.queue.push(args);
  }) as FpixelQueueFunction;
  queue.loaded = true;
  queue.push = queue;
  queue.queue = [];
  queue.version = "2.0";
  return queue;
};

const _createBrowserLoader =
  (
    pixelId: string,
    getWindow: () => FpixelWindow | undefined,
    appendScript: (src: string) => void,
  ) =>
  (): void => {
    const target = getWindow();
    if (!target) return;
    if (!target.fbq) target.fbq = _createQueue();
    target._fbq ||= target.fbq;
    if (target.__oneTranscribeFpixelLoaded) return;
    target.__oneTranscribeFpixelLoaded = true;
    appendScript("https://connect.facebook.net/en_US/fbevents.js");
    target.fbq("init", pixelId);
  };

const _consentCookie = "1t_consent";

export const hasMarketingConsent = (): boolean => {
  if (typeof document === "undefined") return false;
  const stored = document.cookie
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.startsWith(`${_consentCookie}=`))
    ?.slice(_consentCookie.length + 1);
  if (stored === "granted") return true;
  if (stored === "denied") return false;

  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    return !(
      timezone.startsWith("Europe/") ||
      [
        "Atlantic/Reykjavik",
        "Atlantic/Canary",
        "Atlantic/Madeira",
        "Atlantic/Azores",
      ].includes(timezone)
    );
  } catch {
    return false;
  }
};

export const createFpixelClient = (
  pixelId: string,
  dependencies: FpixelClientDependencies = {},
) => {
  const getWindow = dependencies.getWindow || _browserWindow;
  const canTrack = dependencies.canTrack || hasMarketingConsent;
  const load =
    dependencies.load ||
    _createBrowserLoader(
      pixelId,
      getWindow,
      dependencies.appendScript || _appendBrowserScript,
    );
  const call = (...args: unknown[]): void => {
    if (!pixelId || !canTrack()) return;
    load();
    (dependencies.fpixel || getWindow()?.fbq)?.(...args);
  };

  return {
    pageview: () => {
      call("track", "PageView");
    },
    event: (
      name: string,
      data: Record<string, unknown> = {},
      options?: { eventID: string },
    ) => {
      if (options) call("track", name, data, options);
      else call("track", name, data);
    },
  };
};

export type FpixelClient = ReturnType<typeof createFpixelClient>;
